import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Share2, Download, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import { drawShareCanvas, pickLine, downloadCanvas, shareCanvas } from '../lib/shareCard';

const ShareCard = ({ onDone }) => {
  const canvasRef = useRef(null);
  const [line, setLine] = useState(() => pickLine());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const draw = async () => {
      if (!canvasRef.current) return;
      setLoading(true);
      try {
        await drawShareCanvas(canvasRef.current, line);
      } catch {
        if (!cancelled) toast.error("Couldn't build share image. Try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    draw();
    return () => { cancelled = true; };
  }, [line]);

  const handleRefresh = () => {
    let next = pickLine();
    if (next === line) next = pickLine();
    setLine(next);
  };

  const handleShare = async () => {
    if (!canvasRef.current) return;
    try {
      const result = await shareCanvas(canvasRef.current, line);
      if (result.method === 'download') toast.success('Saved to your device.');
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error("Couldn't share. Try downloading instead.");
    }
  };

  const handleDownload = async () => {
    if (!canvasRef.current) return;
    await downloadCanvas(canvasRef.current);
    toast.success('Saved to your device.');
  };

  return (
    <Card className="card-surface border-[#D4AF37]/40 bg-gradient-to-br from-[#1a0f2e] to-[#0A0A0A] mb-6" data-testid="share-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Outfit' }}>
          <span className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#D4AF37]" />
            Share that you got help today
          </span>
          <button
            type="button"
            onClick={onDone}
            className="text-[#71717A] hover:text-[#A1A1AA]"
            data-testid="share-card-close"
            aria-label="Close share card"
          >
            <X className="w-5 h-5" />
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-[#A1A1AA]">
          A private way to share your moment — no message content, just a soft reminder for someone else who might need to see it.
        </p>

        <div
          className="rounded-2xl overflow-hidden border border-[#27272A] bg-[#0A0A0A] aspect-square w-full max-w-md mx-auto relative"
          data-testid="share-card-preview"
        >
          <canvas
            ref={canvasRef}
            className="block w-full h-full rounded-2xl"
            style={{ display: 'block' }}
            data-testid="share-card-canvas"
          />
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-[#A1A1AA] text-sm">
              Drawing...
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3 justify-center">
          <Button
            onClick={handleShare}
            disabled={loading}
            className="btn-gold rounded-full gap-2 px-6"
            data-testid="share-card-share"
          >
            <Share2 className="w-4 h-4" />
            Share
          </Button>
          <Button
            onClick={handleDownload}
            disabled={loading}
            variant="outline"
            className="rounded-full gap-2 px-6 border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6] hover:text-white"
            data-testid="share-card-download"
          >
            <Download className="w-4 h-4" />
            Download
          </Button>
          <Button
            onClick={handleRefresh}
            disabled={loading}
            variant="ghost"
            className="rounded-full gap-2 px-6 text-[#A1A1AA] hover:text-[#F9FAFB]"
            data-testid="share-card-refresh"
          >
            <RefreshCw className="w-4 h-4" />
            New line
          </Button>
        </div>

        <div className="text-center">
          <button
            type="button"
            onClick={onDone}
            className="text-xs text-[#71717A] hover:text-[#A1A1AA] px-3 py-1"
            data-testid="share-card-skip"
          >
            Maybe later — take me home
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ShareCard;
